/**
 * The booking site's social links. Stored on the employee or the business, and
 * rendered by the public pages' socials row.
 */
export interface BookingSiteSocials {
  instagramHandle: string | null;
  tiktokHandle: string | null;
  facebookUrl: string | null;
  websiteUrl: string | null;
}

/**
 * Profile URLs per site, told apart from share links by shape rather than by a
 * list: a profile is a single path segment, so /p/CODE, /stories/user/… and
 * /t/CODE cannot match.
 *
 * The two forms are kept apart because only one of them is ambiguous. A marked
 * handle says which it is, so /@video is the artist named video whatever the
 * site keeps that word for elsewhere; a bare segment could be either, and gets
 * the reserved check.
 */
const PROFILE_URLS = {
  instagram: {
    marked: [
      /^(?:https?:\/\/)?(?:www\.|m\.)?instagram\.com\/@([^/?#]+)\/?(?:[?#]|$)/i,
    ],
    bare: [
      /^(?:https?:\/\/)?(?:www\.|m\.)?instagram\.com\/([^/?#]+)\/?(?:[?#]|$)/i,
    ],
  },
  tiktok: {
    // Marked, so a /video/… path may follow it.
    marked: [/^(?:https?:\/\/)?(?:www\.|m\.)?tiktok\.com\/@([^/?#]+)/i],
    // People leave the @ off, which is still a single segment.
    bare: [
      /^(?:https?:\/\/)?(?:www\.|m\.)?tiktok\.com\/([^/?#]+)\/?(?:[?#]|$)/i,
    ],
  },
} as const;

/** Single segments the sites keep for themselves — no profile lives at one. */
const RESERVED = new Set([
  'about',
  'accounts',
  'direct',
  'discover',
  'explore',
  'foryou',
  'legal',
  'live',
  'music',
  'p',
  'privacy',
  'reel',
  'reels',
  'search',
  'stories',
  't',
  'tag',
  'tv',
  'upload',
  'video',
]);

/**
 * Instagram's own rule: up to 30 of letters, digits, period and underscore,
 * with no leading or trailing period and no two in a row.
 */
export const INSTAGRAM_HANDLE_PATTERN =
  /^(?!\.)(?!.*\.\.)(?!.*\.$)[a-zA-Z0-9._]{1,30}$/;

/** TikTok's, which also refuses a leading or trailing underscore. */
export const TIKTOK_HANDLE_PATTERN =
  /^(?![._])(?!.*\.\.)(?!.*[._]$)[a-zA-Z0-9._]{2,24}$/;

const stripAt = (value: string) => value.replace(/^@+/, '');

const matchIn = (patterns: readonly RegExp[], value: string) =>
  patterns.reduce<string | undefined>(
    (found, pattern) => found ?? pattern.exec(value)?.[1],
    undefined,
  );

/**
 * People paste the profile URL, but we store and render the bare handle. Only
 * a URL we recognize is cut down, and never to a name the site reserves:
 * anything else is passed on as typed, so junk is rejected by validation
 * rather than silently turned into a handle that belongs to somebody else.
 */
const handleFrom =
  (site: keyof typeof PROFILE_URLS) =>
  (value: string): string => {
    const trimmed = value.trim();

    const marked = matchIn(PROFILE_URLS[site].marked, trimmed);
    if (marked) return stripAt(marked);

    const bare = matchIn(PROFILE_URLS[site].bare, trimmed);
    if (!bare) return stripAt(trimmed);

    const handle = stripAt(bare);
    return RESERVED.has(handle.toLowerCase()) ? trimmed : handle;
  };

export const toInstagramHandle = handleFrom('instagram');
export const toTiktokHandle = handleFrom('tiktok');

/** A dot and no spaces — enough to tell a host from a word somebody typed. */
const LOOKS_LIKE_HOST = /^[^\s/?#]+\.[^\s/?#]+/;

const hasScheme = (value: string) => /^https?:\/\//i.test(value);

/**
 * A host with no scheme is a relative href once it reaches the booking site.
 * A bare word is not a host, so it is left as typed and refused — turning
 * `joshuaalvarez06` into `https://joshuaalvarez06` only mangles the field and
 * fails validation anyway.
 */
export function toAbsoluteUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || hasScheme(trimmed)) return trimmed;
  return LOOKS_LIKE_HOST.test(trimmed) ? `https://${trimmed}` : trimmed;
}

/**
 * Facebook's own username rule: letters, digits and periods, at least five of
 * them. No underscore and no hyphen, unlike the two handle patterns above.
 */
const FACEBOOK_USERNAME = /^[a-zA-Z0-9.]{5,}$/;

/** A pasted link carries a path, query or fragment. A username carries none. */
const HAS_PATH = /[/?#]/;

/**
 * Facebook stores a URL rather than a handle — pages live at several shapes,
 * including /pages/Name/1234 — but people type the name from their own page's
 * link, so a bare username is completed.
 *
 * Usernames take periods, so `john.doe` is host-shaped as well as
 * username-shaped. It reads as the username here: this is the Facebook field
 * and a site has its own, and reading it as a host published
 * `https://john.doe`, which validates and links nowhere. A bare word that is
 * not a username still falls through to the host rule, so a scheme-less host
 * is not stored as a relative href.
 */
export function toFacebookUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || hasScheme(trimmed) || HAS_PATH.test(trimmed)) {
    return toAbsoluteUrl(trimmed);
  }

  const username = trimmed.replace(/^@+/, '');
  return FACEBOOK_USERNAME.test(username)
    ? `https://facebook.com/${username}`
    : toAbsoluteUrl(trimmed);
}

/** How each field is normalized. The one authority on which rule a field takes. */
export const SOCIAL_NORMALIZERS: Record<
  keyof BookingSiteSocials,
  (value: string) => string
> = {
  instagramHandle: toInstagramHandle,
  tiktokHandle: toTiktokHandle,
  facebookUrl: toFacebookUrl,
  websiteUrl: toAbsoluteUrl,
};

/** An emptied field clears the value; a corrupt one counts as emptied. */
export function normalizeSocial(
  field: keyof BookingSiteSocials,
  value: string | null | undefined,
): string | null {
  if (typeof value !== 'string' || !value) return null;
  return SOCIAL_NORMALIZERS[field](value) || null;
}

/**
 * Every field at once, for a surface whose save can be triggered from a
 * focused input — the blur that normally tidies a field never runs, so the
 * value on screen would otherwise disagree with the one stored.
 */
export function normalizeSocials(
  socials: BookingSiteSocials,
): BookingSiteSocials {
  return {
    instagramHandle: normalizeSocial(
      'instagramHandle',
      socials.instagramHandle,
    ),
    tiktokHandle: normalizeSocial('tiktokHandle', socials.tiktokHandle),
    facebookUrl: normalizeSocial('facebookUrl', socials.facebookUrl),
    websiteUrl: normalizeSocial('websiteUrl', socials.websiteUrl),
  };
}
